import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { LICENSE_STATUS_NONE } from "@/v2-backoffice-client/license/license.constants"

// Seats follow the same math as the self-service invite disclaimer
// (acc.CustomerSeatUsage): every non-deleted user plus every open
// invitation occupies one seat against the licensed cap — LicensedSeats,
// the summed seat caps of the customer's live lic.Licenses rows.
export type CustomerSeatInput = Pick<
  CustomerFragment,
  "UserCount" | "PendingInvitationCount" | "LicenseStatusCode" | "LicensedSeats"
>

/**
 * The seat-pressure bucket a customer falls into. Exactly one applies.
 *
 * The values are wire literals: the customers list filter sends them to
 * the server, where customersFilterRegistry (v2-backoffice-api
 * customer.api.go) compiles an equivalent SQL CASE. Keep both sides in
 * sync when touching the derivation below.
 */
export const CUSTOMER_SEAT_STATUSES = {
  /** More seats occupied than licensed — expect this on the invoice. */
  OVER_LIMIT: "OverLimit",
  /** Live licenses without a seat cap — occupancy can't overrun. */
  UNCAPPED: "Uncapped",
  /** Occupied seats fit within the licensed cap. */
  OK: "Ok",
} as const

type CustomerSeatStatus =
  (typeof CUSTOMER_SEAT_STATUSES)[keyof typeof CUSTOMER_SEAT_STATUSES]

export type CustomerSeatFacts = {
  seatsUsed: number
  userCount: number
  pendingInvitationCount: number
  /** Summed seat caps of live licenses; `null` = uncapped. */
  licensedSeats: number | null
  /** `null` when the customer holds no license — seat pressure doesn't apply. */
  status: CustomerSeatStatus | null
}

export function deriveCustomerSeatFacts(
  customer: CustomerSeatInput,
): CustomerSeatFacts {
  const userCount = customer.UserCount
  const pendingInvitationCount = customer.PendingInvitationCount ?? 0
  const seatsUsed = userCount + pendingInvitationCount
  const licensedSeats = customer.LicensedSeats ?? null

  return {
    seatsUsed,
    userCount,
    pendingInvitationCount,
    licensedSeats,
    status: deriveStatus(customer, seatsUsed, licensedSeats),
  }
}

// Mirrored as a SQL CASE in customersFilterRegistry (v2-backoffice-api)
// so filtering the list agrees with what the badge shows.
function deriveStatus(
  customer: CustomerSeatInput,
  seatsUsed: number,
  licensedSeats: number | null,
): CustomerSeatStatus | null {
  const statusCode = customer.LicenseStatusCode ?? LICENSE_STATUS_NONE
  if (statusCode === LICENSE_STATUS_NONE) return null
  if (licensedSeats == null) return CUSTOMER_SEAT_STATUSES.UNCAPPED
  if (seatsUsed > licensedSeats) return CUSTOMER_SEAT_STATUSES.OVER_LIMIT
  return CUSTOMER_SEAT_STATUSES.OK
}

/** One-line explanation for tooltips/exports: "8 seats (6 users + 2 invitations) of 5 licensed". */
export function describeCustomerSeatFacts(facts: CustomerSeatFacts): string {
  const occupied = `${facts.seatsUsed} seats (${facts.userCount} users + ${facts.pendingInvitationCount} invitations)`
  if (facts.licensedSeats == null) {
    return `${occupied}, uncapped license`
  }
  return `${occupied} of ${facts.licensedSeats} licensed`
}
